// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { code128Svg, qrSvg } from './labelCodes'
import { solveLabelScale, LABEL_FIT_BOUNDS } from './labelFit'
import type { LabelConfig } from '../../api/client'
import { formatPhone } from '../../api/format'
import { carrierPrintLabel as carrierLabel } from '../../lib/carriers'

/**
 * Printable shipping label (Sprint 18). Size-aware (4x6 / 4x3 / letter),
 * renders the pharmacy logo + name, destination, handling flags, and a
 * MANDATORY Code128 barcode + QR (both encode the tracking/order ref).
 * Drug name is never rendered (PHI). Used in a print view.
 */

export interface LabelData {
  carrier: string
  trackingNumber: string | null
  orderRef: string | null
  pharmacyName: string
  pharmacyLogoUrl: string | null
  fromLine: string | null
  recipientName: string | null
  toLine1: string | null
  toCityStateZip: string | null
  toPhone: string | null
  coldChain: boolean
  signature: boolean
  adultSignature: boolean
  saturday: boolean
  instructions: string | null
  rxNumber: string | null
  rxNumbers?: string[] | null   // ASP-18: every Rx in the package (fill suffix where known)
  serviceLevel?: string | null  // ASP label v2 (match courier label)
  shipDate?: string | null      // shipment creation date MM/DD/YYYY
  isManual: boolean
  copay: number | null      // patient pay amount from the Rx, when known
  copayItems?: { rx_number: string; copay: number; collected?: boolean; due?: number }[] | null   // per-Rx copay + paid/collect status + amount still due
  copayUnitemised?: number   // debt with no printable rx_number (drifted claim) — printed as its own line
  copayCollected?: boolean  // D-SHIP-COPAY-COLLECTED: sale completed in PioneerRx (all members)
  copayPaidAmount?: number | null   // dollars actually paid (Rx.PatientPaidAmount)
  copayState?: 'none' | 'paid' | 'needed'   // D-SHIP-COPAY-MULTI: the copay slot state from label-data
  copayCollectTotal?: number   // driver-collect amount (pending Rx only)
  // D-SOC-1: destination is a Site of Care facility (SNF / board & care / AIC ...). null = residential.
  facilityType?: string | null
  facilityTypeLabel?: string | null
  facilityName?: string | null
  facilityPhone?: string | null
  // D-SHIP-FAC-1: facility package — the recipient IS the facility; each line says which resident gets what
  shipmentKind?: string | null
  rxLines?: { rxNumber: string | null; patientName: string | null; drugName: string | null }[] | null
}

const SIZES = {
  '4x6':    { w: 384, h: 576,  mmW: 100, mmH: 150 },
  '4x3':    { w: 384, h: 288,  mmW: 100, mmH: 76  },
  'letter': { w: 816, h: 1056, mmW: 216, mmH: 279 },
}

// Custom text elements a pharmacy adds in Shipping settings, placed per region.
// LBL-FONT (Patrick 2026-08-02): global label scale so a busy 4x6 (cold chain + COD + all tags +
// multi-Rx) fits without overflow or wrapped lines, applied on top of the per-pharmacy base_font_px.
const LABEL_SCALE = 0.82
// Tag icons — inline SVG so they print identically regardless of icon fonts; currentColor inherits flag ink.
const _ic = { width: 14, height: 14, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const }
const SnowflakeIcon = () => <svg {..._ic}><path d="M12 2v20M4.9 6.5l14.2 11M19.1 6.5L4.9 17.5" /></svg>
const SignIcon = () => <svg {..._ic}><path d="M4 20h16M4 16l9-9 3 3-9 9H4z" /></svg>
const ShieldIcon = () => <svg {..._ic}><path d="M12 3l7 3v5c0 4-3 7-7 8-4-1-7-4-7-8V6z" /><path d="M9 12l2 2 4-4" /></svg>
const AlertIcon = () => <svg {..._ic}><path d="M12 3l9 16H3z" /><path d="M12 10v4M12 16.5v.5" /></svg>

// 'banner' renders as a full-width accent strip; 'bold'/'normal' as text rows.
function CustomElements({ config, region, accent, compact }: { config: LabelConfig; region: 'header' | 'body' | 'footer'; accent: string; compact: boolean }) {
  const els = (config.custom_elements ?? []).filter(e => e.region === region && e.text)
  const fz = (n: number) => Math.round(n * LABEL_SCALE * ((config.base_font_px ?? 14) / 14) * 10) / 10
  const elSize = (e: { size?: number | null }, dflt: number) => e.size ? fz(e.size) : fz(dflt)
  const elAlign = (e: { align?: 'left' | 'center' | 'right' | null }): 'left' | 'center' | 'right' => e.align || 'left' 
  if (!els.length) return null
  return (<>
    {els.map(e => e.emphasis === 'banner' ? (
      <div key={e.id} style={{ background: accent, color: '#fff', textAlign: 'center', fontWeight: 800, letterSpacing: 1, padding: compact ? '3px 8px' : '5px 12px', fontSize: elSize(e, compact ? 11 : 14.5) }}>
        {e.text}
      </div>
    ) : (
      <div key={e.id} style={{ padding: compact ? '3px 10px' : '4px 14px', fontSize: elSize(e, compact ? 10.5 : 14), lineHeight: 1.35, fontWeight: e.emphasis === 'bold' ? 700 : 400, textAlign: elAlign(e) }}>
        {e.text}
      </div>
    ))}
  </>)
}

/** Driver-facing words for the facility flag: the TYPE label ("SNF / Nursing home"), never the code. */
function facilityFlag(label: string | null | undefined, code: string | null | undefined): string {
  const l = (label || code || 'FACILITY').split(' — ')[0]
  return l.length > 26 ? l.slice(0, 26) : l
}

export default function ShippingLabel({ config, data }: { config: LabelConfig; data: LabelData }) {
  const dim = SIZES[config.size] ?? SIZES['4x6']
  const accent = '#111111'  // B&W label printer: force black. A configured accent colour only prints as muddy grey, so bold=dark ink, everything else=light ink carries the hierarchy instead.
  const compact = config.size === '4x3'

  const logoUrl = config.logo_mode === 'custom' ? config.custom_logo_url
    : config.logo_mode === 'pharmacy' ? data.pharmacyLogoUrl
    : null

  const codeValue = (config.barcode_value === 'order' ? data.orderRef : data.trackingNumber)
    || data.trackingNumber || data.orderRef || 'NO-REF'
  // D-SHIP-SORT (Patrick 2026-08-09): opt-in sorter aids next to the service type — a large
  // destination ZIP (depot sorters read it) + a QR of the ORDER number (scannable). Rendered
  // as a SYNCHRONOUS inline SVG exactly like the barcode, so auto-print's wait-for-assets →
  // print-once timing is unaffected (no async image to load). Both omit gracefully on
  // missing/unparseable input — a wrong ZIP would misdirect a sorter, so we only show a
  // confidently-parsed 5-digit (or ZIP+4) code, else nothing.
  const sortZip = config.show_sort_qr ? ((data.toCityStateZip ?? '').match(/\b(\d{5})(?:-\d{4})?\b/)?.[1] ?? null) : null
  const sortQr = (config.show_sort_qr && data.orderRef) ? qrSvg(data.orderRef, { size: 132 }) : null

  // D-SHIP-5: full-width barcode. Code 128-B uses 11 modules per symbol
  // (start + data + checksum + stop + final bar). Fit the bars to the label's
  // inner width so the barcode spans the whole label at any size, instead of a
  // fixed narrow module width that left it small on the left. Clamp so a very
  // long reference stays readable and a short one doesn't render absurdly fat.
  const sidePad = compact ? 20 : 28                     // 10px/14px each side
  const innerW = dim.w - sidePad
  // D-SHIP-SORT feedback (Mario 2026-08-10): the sorter QR sits beside the barcode, not up in the
  // service-type block. Reserve its footprint HERE so the barcode is GENERATED at the right module
  // width for the space it actually gets. Squashing a full-width barcode with CSS instead would
  // narrow every bar by the same ~20%, and Code128 scan reliability is a function of the narrowest
  // bar — a depot scanner failing on a worn thermal print is not a defect we would see in preview.
  const sortQrSide = compact ? 44 : 60                  // square, keyed to the barcode block height
  const sortQrGap = compact ? 8 : 10
  const barcodeW = sortQr ? innerW - sortQrSide - sortQrGap : innerW
  const symbolCount = codeValue.length + 3              // start + checksum + stop
  const modulesTotal = symbolCount * 11 + 13 + 20       // + stop tail + quiet zone (10 each side)
  const fitModuleWidth = Math.max(1.1, Math.min(3.2, barcodeW / modulesTotal))
  const barcode = code128Svg(codeValue, { height: compact ? 46 : 66, moduleWidth: fitModuleWidth })

  // D-SHIP-5 / one-label guarantee: the label is a FIXED-SIZE print target and must
  // never spill to a second page or clip. A busy package (7-line COD list + full-
  // width barcode + multi-Rx list + instructions) can exceed the fixed height, so
  // we measure the rendered content and, if it overflows, scale it down to fit —
  // everything stays on ONE label, shrunk just enough, never cut off.
  const contentRef = useRef<HTMLDivElement | null>(null)
  // D-SHIP-7 (Patrick): user-controlled base font size. All label text scales from
  // base_font_px (design default 14). The fit below still guarantees ONE label:
  // bigger base grows text while there's room and shrinks when it must.
  const fz = (n: number) => Math.round(n * LABEL_SCALE * ((config.base_font_px ?? 14) / 14) * 10) / 10

  // FIT-TO-FILL, SOLVED (2026-08-04) — replaces the measure→setState→re-measure loop.
  //
  // Why it had to change: the pre-transform width is 1/scale, so changing the scale
  // re-wraps text and CHANGES the measured height. That feedback could oscillate
  // forever (the blank print tab, capped at 12 passes by #2303) and — the part the cap
  // did not fix — it settles wherever the budget runs out, NOT at a size that fits.
  // Live on a real 4x6: scale 1.33, content at 136% of the label, 205px cut off the
  // bottom by overflow:hidden — INCLUDING THE BARCODE. Also `scrollHeight / scale` is
  // simply wrong: scrollHeight is a layout metric, unaffected by the element's own
  // transform, so there is nothing to divide out.
  //
  // Instead: probe the live DOM synchronously inside ONE layout pass (see labelFit.ts).
  // renderedAt(s) applies a candidate scale and returns the resulting rendered height,
  // and a bounded binary search returns the largest scale that still fits. No React
  // state in the loop → it cannot oscillate, cannot spin the tab, and cannot clip.
  const fitDeps = [
    config.base_font_px, config.size, compact, dim.h, accent,
    data.rxNumbers?.length, data.instructions, config.consult_message,
    data.copayItems?.length, data.copayState, data.coldChain, data.toLine1, data.recipientName,
    codeValue, (config.custom_elements ?? []).length,
    config.show_sort_qr, data.toCityStateZip, data.orderRef,
    data.facilityName, data.facilityTypeLabel,
    data.shipmentKind, data.rxLines?.length,
  ]
  const [fitTick, setFitTick] = useState(0)
  const [fontsReady, setFontsReady] = useState(false)
  useEffect(() => {
    let alive = true
    const fonts = (document as any).fonts?.ready
    if (fonts?.then) fonts.then(() => { if (alive) setFontsReady(true) })
    else setFontsReady(true)
    return () => { alive = false }
  }, [])
  useLayoutEffect(() => {
    const content = contentRef.current
    if (!content) return
    // 2px border top+bottom on the border-box canvas, plus 1% so the last line never
    // touches the border.
    const target = (dim.h - 4) * 0.99
    const apply = (s: number) => {
      content.style.width = `${100 / s}%`
      content.style.transform = s === 1 ? '' : `scale(${s})`
    }
    const renderedAt = (s: number) => { apply(s); return content.scrollHeight * s }
    // Floor keeps Patrick's #2304 behaviour: a large base font must be allowed to
    // shrink to its design-equivalent size, or it cannot fit at all.
    const min = Math.min(LABEL_FIT_BOUNDS.min, 14 / (config.base_font_px ?? 14))
    apply(solveLabelScale(renderedAt, target, { min, max: LABEL_FIT_BOUNDS.max }))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...fitDeps, fitTick, fontsReady])
  const refit = () => setFitTick(t => t + 1)

  // Fill the PHYSICAL label. The label is authored on a fixed px design canvas
  // (dim.w x dim.h) with tuned fonts; the OUTER box is sized in INCHES so it occupies
  // the entire 4x6 stock (matching the @page), and the px canvas is scaled to fill
  // it. This fixes the label printing small in the top corner: a px-sized box is not
  // guaranteed to equal the physical label across printer DPIs, but an inch-sized box
  // is. Scale = (inches * 96px/in) / designPx; since 384:576 is already 4:6 it's
  // uniform, and letter maps too.
  const PX_PER_MM = 96 / 25.4
  const fillW = (dim.mmW * PX_PER_MM) / dim.w
  const fillH = (dim.mmH * PX_PER_MM) / dim.h

  return (
    <div style={{
      width: `${dim.mmW}mm`, height: `${dim.mmH}mm`, boxSizing: 'border-box',
      background: '#fff', overflow: 'hidden', position: 'relative',
    }}>
    <div style={{
      position: 'absolute', top: 0, left: 0, width: dim.w, height: dim.h,
      transform: `scale(${fillW}, ${fillH})`, transformOrigin: 'top left',
      background: '#fff', border: `2px solid ${accent}`,
      fontFamily: 'Helvetica Neue, Arial, sans-serif', color: '#111', overflow: 'hidden',
      printColorAdjust: 'exact', WebkitPrintColorAdjust: 'exact',   // force any fills to print (B&W label printer)
      boxSizing: 'border-box',
    }}>
    <div ref={contentRef} style={{
      // Scaled uniformly from the top-left so the package always fills exactly ONE
      // label. width/transform are written imperatively by the fit pass above (that
      // is what lets it probe candidate scales without re-rendering).
      width: '100%',
      transformOrigin: 'top left',
      display: 'flex', flexDirection: 'column',
      // height:auto (no minHeight) so scrollHeight is the TRUE natural content height
      // — that's what lets the fit detect spare room and scale UP to fill.
      boxSizing: 'border-box',
      // A long unbroken string (address, custom text) must never widen the label.
      // break-word, NOT anywhere: `anywhere` also shrinks an element's min-content
      // width, which collapses the flex columns in the Deliver-to block and breaks
      // ordinary words mid-syllable ("SLAU/SON"). break-word only kicks in for a
      // token that genuinely cannot fit.
      overflowWrap: 'break-word',
    }}>
      {/* ASP-19: prominent refrigerate banner at the very top for cold-chain shipments.
          Black bar + bold WHITE text (Patrick 2026-08-05). Snowflakes are monochrome SVG
          (white stroke); print-color-adjust:exact (here + on the container) forces the black
          fill to print on a B&W label printer even when "Background graphics" is off. */}
      {config.show_refrigerate_banner && data.coldChain && (
        <div style={{ background: accent, color: '#fff', textAlign: 'center', fontWeight: 900, letterSpacing: 1.5, padding: compact ? '4px 8px' : '6px 12px', fontSize: fz(compact ? 12.5 : 16), display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, printColorAdjust: 'exact', WebkitPrintColorAdjust: 'exact' }}>
          <svg width={fz(compact ? 13 : 15)} height={fz(compact ? 13 : 15)} viewBox="0 0 24 24" aria-hidden="true" style={{ flex: '0 0 auto' }}><path d="M12 1v22M3 6.5l18 11M21 6.5l-18 11M12 4.5 8.5 8M12 4.5 15.5 8M12 19.5 8.5 16M12 19.5 15.5 16M3.2 12 7 9.8M3.2 12 7 14.2M20.8 12 17 9.8M20.8 12 17 14.2" stroke="#fff" strokeWidth="1.6" strokeLinecap="round" fill="none"/></svg>
          REFRIGERATE UPON ARRIVAL
          <svg width={fz(compact ? 13 : 15)} height={fz(compact ? 13 : 15)} viewBox="0 0 24 24" aria-hidden="true" style={{ flex: '0 0 auto' }}><path d="M12 1v22M3 6.5l18 11M21 6.5l-18 11M12 4.5 8.5 8M12 4.5 15.5 8M12 19.5 8.5 16M12 19.5 15.5 16M3.2 12 7 9.8M3.2 12 7 14.2M20.8 12 17 9.8M20.8 12 17 14.2" stroke="#fff" strokeWidth="1.6" strokeLinecap="round" fill="none"/></svg>
        </div>
      )}
      {/* header band */}
      <div style={{ background: '#fff', color: '#111111', borderBottom: `2px solid ${accent}`, padding: compact ? '6px 10px' : '10px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {logoUrl && <img src={logoUrl} alt="" onLoad={refit} onError={refit} style={{ height: compact ? 22 : 30, maxWidth: 120, objectFit: 'contain', background: '#fff', borderRadius: 3, padding: 2 }} />}
          <div style={{ fontSize: fz(compact ? 13.5 : 17), fontWeight: 800, whiteSpace: 'nowrap', overflow: 'hidden' }}>{data.pharmacyName}</div>
        </div>
        <div style={{ fontSize: fz(compact ? 10 : 13.5), fontWeight: 800, textAlign: 'right', letterSpacing: 0.5 }}>{carrierLabel(data.carrier)}</div>
      </div>
      {/* D-SOC-1 (Patrick 2026-09-04): a package going to a FACILITY says so in the header, so the
          driver never treats an SNF like a house. Type as a solid flag, facility name beside it. */}
      {data.facilityName && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: compact ? '4px 10px' : '6px 14px', borderBottom: `2px solid ${accent}`, printColorAdjust: 'exact', WebkitPrintColorAdjust: 'exact' }}>
          <span style={{ background: accent, color: '#fff', fontWeight: 900, letterSpacing: 0.6, textTransform: 'uppercase', fontSize: fz(compact ? 9.5 : 11), padding: compact ? '2px 6px' : '3px 8px', borderRadius: 3, whiteSpace: 'nowrap', flex: '0 0 auto' }}>
            {facilityFlag(data.facilityTypeLabel, data.facilityType)}
          </span>
          <span style={{ fontWeight: 800, fontSize: fz(compact ? 11 : 13.5), lineHeight: 1.2, overflowWrap: 'normal' }}>{data.facilityName}</span>
        </div>
      )}

      <CustomElements config={config} region="header" accent={accent} compact={compact} />
      {/* handling flags */}
      {config.show_handling_flags && (data.coldChain || data.signature || data.adultSignature || data.saturday) ? (
        <div style={{ display: 'flex', gap: 5, padding: compact ? '5px 10px' : '7px 14px', borderBottom: `2px solid ${accent}`, flexWrap: 'wrap' }}>
          {data.coldChain && <Flag text="Cold chain" accent={accent} solid icon={<SnowflakeIcon />} />}
          {data.signature && <Flag text="Signature" accent={accent} icon={<SignIcon />} />}
          {data.adultSignature && <Flag text="21+ Adult" accent={accent} icon={<ShieldIcon />} />}
          {data.saturday && <Flag text="Special handling" accent={accent} icon={<AlertIcon />} />}
        </div>
      ) : null}

      {/* D-SHIP-5: itemized COD copay block (opt-in via show_copay; default off).
          Per-Rx lines that sum to the printed total. Multi-Rx shows the breakdown;
          a single nonzero copay keeps the compact one-line form. $0/absent copays
          are omitted (nothing to collect). Falls back to the single total when
          copayItems is absent (older shipment). */}
      {/* from */}
      {config.show_from_block && data.fromLine && (
        <div style={{ padding: compact ? '3px 10px' : '4px 14px', fontSize: fz(compact ? 10 : 10.5), color: '#555' }}>From: {data.fromLine}</div>
      )}

      {/* S2 label v2 (Patrick 2026-08-02): compact COD box (left) + service/date/order (right)
          in ONE row. No-COD fallback: the service block spans the whole row. */}
      {(() => {
        const items = (data.copayItems ?? []).filter(i => i.copay > 0)
        const total = items.length ? items.reduce((s, i) => s + i.copay, 0)
          : (data.copay != null && data.copay > 0 ? data.copay : 0)
        // D-SHIP-COPAY-3STATE (Patrick 2026-08-04, approved mockup): the copay slot renders ONE
        // of three states, sent by label-data as the single source: 'none' = No copay (nothing
        // owed — the common Medicaid case), 'paid' = settled, 'needed' = collect (each Rx +
        // total). Gated by show_copay.
        //
        // The fallback covers only the settings PREVIEW and pre-fix cached data, and it MIRRORS
        // the server rule (DeriveCopayState) including the part that is easy to drop: settlement
        // must cover the WHOLE debt, not merely exist. `copayPaidAmount` is the settled members'
        // copay, so on a drifted order it is less than `total` and this correctly says "needed" —
        // the earlier `> 0` test said "paid" there, failing open in the one direction that loses
        // money. It is unreachable while the server sends a state; it must still be right.
        const state: 'none' | 'paid' | 'needed' = data.copayState
          ?? (total <= 0
              ? 'none'
              : (data.copayCollected && (data.copayPaidAmount ?? 0) >= total ? 'paid' : 'needed'))
        const none = config.show_copay && state === 'none'
        const paid = config.show_copay && state === 'paid'
        const needed = config.show_copay && state === 'needed' && total > 0
        // The driver's number. The server sends it (fail-closed: the larger of the itemised
        // shortfall and owed-minus-paid, so a member with no claim row still gets collected on)
        // and it is authoritative — do NOT max() it against the client estimate below, which is
        // all-or-nothing per line and would over-collect on a partially-paid Rx.
        // The fallback is for preview / pre-fix data only, and fails in the same direction:
        // the OLD one printed `total` whenever no line was flagged pending, or 0 via the server,
        // which is how "COPAY NEEDED ... Collect $0.00" reached a real label.
        const pendingSum = items.filter(i => i.collected === false).reduce((s, i) => s + i.copay, 0)
        const collectTotal = data.copayCollectTotal
          ?? Math.max(pendingSum, total - (data.copayPaidAmount ?? 0), 0)
        // What the printed lines cannot account for. Server-sent when available; otherwise derived
        // so the block still balances on older data.
        const itemisedDue = items.reduce((s, i) => s + (i.collected ? 0 : (i.due ?? i.copay)), 0)
        const unitemised = data.copayUnitemised ?? Math.max(collectTotal - itemisedDue, 0)
        const showSvc = !!(data.serviceLevel || data.shipDate)
        if (!none && !paid && !needed && !showSvc) return null
        return (
          <div style={{ display: 'flex', borderTop: `2px solid ${accent}`, borderBottom: `2px solid ${accent}` }}>
            {none && (
              <div style={{ flex: '0 0 auto', padding: compact ? '5px 16px 5px 10px' : '7px 20px 7px 14px', borderRight: '1px solid #ccc', display: 'flex', alignItems: 'center' }}>
                <div style={{ fontSize: fz(compact ? 12 : 15), fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.4 }}>No copay</div>
              </div>
            )}
            {paid && (
              <div style={{ flex: '0 0 auto', padding: compact ? '5px 16px 5px 10px' : '7px 20px 7px 14px', borderRight: '1px solid #ccc', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{ border: `2px solid ${accent}`, borderRadius: 3, padding: compact ? '1px 6px' : '2px 8px', fontWeight: 900, letterSpacing: 1, fontSize: fz(compact ? 12 : 16), textTransform: 'uppercase' }}>PAID</div>
                  <div style={{ lineHeight: 1.3 }}>
                    <div style={{ fontSize: fz(compact ? 9 : 10), fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.5, color: '#555' }}>Copay</div>
                    <div style={{ fontSize: fz(compact ? 11 : 13), fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.3 }}>Copay collected</div>
                  </div>
                </div>
              </div>
            )}
            {needed && (
              <div style={{ flex: '0 0 auto', padding: compact ? '5px 16px 5px 10px' : '7px 20px 7px 14px', color: '#111111', borderRight: '1px solid #ccc' }}>
                <div style={{ fontSize: fz(compact ? 10 : 12), fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.6 }}>Copay needed</div>
                <div style={{ marginTop: 2, fontSize: fz(compact ? 10.5 : 12.5), lineHeight: 1.6 }}>
                  {/* THE LINES MUST SUM TO THE TOTAL. An outstanding line prints what the driver
                      COLLECTS on it (`due`), not the claim — $29 recorded against a $30 claim is
                      $1 at the door. A settled line prints its claim, greyed, purely so the driver
                      can see it was already handled. */}
                  {items.map(i => (
                    <div key={i.rx_number} style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}>
                      <span style={{ minWidth: 50, fontWeight: 800 }}>{i.rx_number}</span>
                      <b style={{ fontVariantNumeric: 'tabular-nums', flex: '1 1 auto', textAlign: 'right' }}>${(i.collected ? i.copay : (i.due ?? i.copay)).toFixed(2)}</b>
                      <span style={{ fontSize: fz(compact ? 8 : 8.5), fontWeight: 800, textTransform: 'uppercase', color: i.collected ? '#888' : '#111', minWidth: 42, textAlign: 'right' }}>{i.collected ? 'paid' : 'collect'}</span>
                    </div>
                  ))}
                  {/* Debt the claims can no longer name (the stamp outlived its claim row). Without
                      this line the block reads "everything PAID, now hand over $16" — see
                      copay_unitemised in ShippingShipmentFunctions. */}
                  {unitemised > 0.004 && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}>
                      <span style={{ minWidth: 50, fontWeight: 800 }}>Balance</span>
                      <b style={{ fontVariantNumeric: 'tabular-nums', flex: '1 1 auto', textAlign: 'right' }}>${unitemised.toFixed(2)}</b>
                      <span style={{ fontSize: fz(compact ? 8 : 8.5), fontWeight: 800, textTransform: 'uppercase', color: '#111', minWidth: 42, textAlign: 'right' }}>collect</span>
                    </div>
                  )}
                  <div style={{ borderTop: '1px solid #999', marginTop: 1, paddingTop: 1, fontWeight: 800, display: 'flex', justifyContent: 'space-between', gap: 10 }}>
                    <span>Collect</span><span style={{ fontVariantNumeric: 'tabular-nums' }}>${collectTotal.toFixed(2)}</span>
                  </div>
                </div>
              </div>
            )}
            {showSvc && (
              <div style={{ flex: '1 1 auto', padding: compact ? '4px 10px 4px 18px' : '7px 14px 7px 26px', display: 'flex', flexDirection: 'column', justifyContent: 'center', whiteSpace: 'nowrap' }}>
                <div style={{ fontSize: fz(compact ? 16 : 22), fontWeight: 900, letterSpacing: 0.8 }}>{data.serviceLevel || ''}</div>
                {/* ZIP stays exactly where it was; the QR moved down beside the barcode, so this
                    block now spans the full column width instead of sharing it. */}
                {sortZip && (
                  <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1, width: '100%', margin: compact ? '3px 0' : '6px 0 5px' }}>
                    <span style={{ fontSize: fz(compact ? 8 : 9), fontWeight: 800, color: '#333', letterSpacing: 0.6 }}>ZIP</span>
                    <span style={{ fontSize: fz(compact ? 18 : 22), fontWeight: 900, letterSpacing: 1, fontVariantNumeric: 'tabular-nums', lineHeight: 1.05 }}>{sortZip}</span>
                  </div>
                )}
                {data.shipDate && <div style={{ fontSize: fz(compact ? 10 : 12), lineHeight: 1.6 }}><span style={{ color: '#555' }}>Date</span> <b>{data.shipDate}</b></div>}
                {data.orderRef && <div style={{ lineHeight: 1.2, marginTop: 2 }}><span style={{ color: '#555', fontSize: fz(compact ? 9 : 11) }}>Order </span><b style={{ fontSize: fz(compact ? 14 : 18), fontWeight: 900, letterSpacing: 0.3 }}>{data.orderRef}</b></div>}
              </div>
            )}
          </div>
        )
      })()}
      <div style={{ padding: compact ? '6px 10px' : '9px 14px', borderBottom: `2px solid ${accent}` }}>
        <div style={lbl}>Deliver to</div>
        {data.facilityName && data.shipmentKind !== 'facility' && (
          <div style={{ fontSize: fz(compact ? 10.5 : 12.5), fontWeight: 700, color: '#333', lineHeight: 1.2 }}>c/o {data.facilityName}{data.facilityPhone ? <span style={{ fontWeight: 500 }}> · {formatPhone(data.facilityPhone)}</span> : null}</div>
        )}
        {/* D-SHIP-COPAY-3STATE (Patrick 2026-08-04): the recipient NAME gets its own full-width
            line and wraps only at spaces — never mid-word (overflowWrap:normal + wordBreak:keep-all
            override the parent break-word). The ADDRESS moves to the line BELOW the name. */}
        <div style={{ fontSize: fz(compact ? 15 : 19), fontWeight: 800, lineHeight: 1.15, overflowWrap: 'normal', wordBreak: 'keep-all' }}>{data.recipientName || '—'}</div>
        <div style={{ fontSize: fz(compact ? 11 : 13.5), fontWeight: 600, lineHeight: 1.4, marginTop: 3 }}>
          {data.toLine1}<br />{data.toCityStateZip}{data.toPhone ? <> · {formatPhone(data.toPhone)}</> : null}
        </div>
      </div>

      <CustomElements config={config} region="body" accent={accent} compact={compact} />
      {/* barcode only — QR removed per Patrick 2026-07-29 (match the courier's own label).
          D-SHIP-5: the SVG is forced to width:100% of the label's inner width so it
          spans the FULL label width (the viewBox keeps the bars crisp when stretched).
          This guarantees full-width regardless of the reference length. */}
      <div style={{ padding: compact ? '6px 10px' : '10px 14px', display: 'flex', alignItems: 'center', gap: sortQr ? sortQrGap : 0 }}>
        {/* minWidth:0 so the barcode column can actually shrink inside the flex row */}
        <div style={{ flex: '1 1 auto', minWidth: 0 }}>
          <div style={{ width: '100%', lineHeight: 0 }}
               dangerouslySetInnerHTML={{ __html: barcode.replace('<svg ', `<svg preserveAspectRatio="none" style="width:100%;height:${compact ? 30 : 42}px;display:block" `) }} />
          {/* caption stays centred under the BARS, not under bars+QR */}
          <div style={{ fontSize: fz(compact ? 12 : 15), fontWeight: 800, letterSpacing: 1.3, fontFamily: 'Courier New, monospace', marginTop: 2, textAlign: 'center' }}>{codeValue}</div>
        </div>
        {sortQr && (
          <div style={{ flex: `0 0 ${sortQrSide}px`, width: sortQrSide, height: sortQrSide, lineHeight: 0 }}
               dangerouslySetInnerHTML={{ __html: sortQr.replace('<svg ', '<svg style="width:100%;height:100%;display:block" ') }} />
        )}
      </div>

      {/* instructions + rx tag */}
      {config.show_instructions && data.instructions && (
        <div style={{ padding: compact ? '5px 10px' : '8px 14px', borderTop: `2px solid ${accent}`, fontSize: fz(compact ? 10 : 13), lineHeight: 1.4, fontWeight: 700, flex: compact ? '0 0 auto' : '1 1 auto' }}>
          <div style={lbl}>Instructions</div>{data.instructions}
        </div>
      )}
      <CustomElements config={config} region="footer" accent={accent} compact={compact} />
      {config.show_consult_message && config.consult_message && (
        <div style={{ padding: compact ? '5px 10px' : '8px 14px', borderTop: `2px solid ${accent}`, fontSize: fz(compact ? 10 : 13.5), lineHeight: 1.35, fontWeight: 400 }}>
          {config.consult_message}
        </div>
      )}
      {/* ASP-18 (Patrick 2026-07-28): print EVERY Rx number in the package. Multi-Rx
          packages get a dedicated block; single-Rx keeps the compact footer tag. */}
      {/* D-SHIP-FAC-1: a facility package prints one line per Rx WITH the resident's name — the
          box goes to the nurses' station, and the label must say who gets what. */}
      {data.shipmentKind === 'facility' && (data.rxLines?.length ?? 0) > 0 && (() => {
        const residents = new Set(data.rxLines!.map(l => l.patientName || '')).size
        return (
          <div style={{ padding: compact ? '5px 10px' : '8px 14px', borderTop: `2px solid ${accent}`, fontSize: fz(compact ? 10 : 13), lineHeight: 1.35 }}>
            <div style={lbl}>Facility package · {data.rxLines!.length} Rx for {residents} resident{residents === 1 ? '' : 's'}</div>
            {data.rxLines!.map((l, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                <span style={{ fontWeight: 900 }}>{l.rxNumber || '—'}</span>
                <span style={{ fontWeight: 700, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{l.patientName || 'Resident'}</span>
              </div>
            ))}
          </div>
        )
      })()}
      {!data.isManual && data.shipmentKind !== 'facility' && (data.rxNumbers?.length ?? 0) > 1 && (
        <div style={{ padding: compact ? '5px 10px' : '8px 14px', borderTop: `2px solid ${accent}`, fontSize: fz(compact ? 10 : 13.5), lineHeight: 1.4 }}>
          <div style={lbl}>Rx in this package ({data.rxNumbers!.length})</div>
          <div style={{ fontWeight: 900 }}>{data.rxNumbers!.join(' · ')}</div>
        </div>
      )}
      <div style={{ fontSize: fz(compact ? 9 : 12), padding: compact ? '3px 10px' : '4px 14px', textAlign: 'left' }}>
        <span style={{ color: '#111', fontWeight: 800 }}>{data.isManual ? 'Non-Rx package'
          : ((data.rxNumbers?.length ?? 0) > 1 ? `${data.rxNumbers!.length} Rx`
            : (data.rxNumber ? `Rx ${data.rxNumber}` : 'Rx delivery'))}</span><span style={{ color: '#555555', fontWeight: 700 }}> · OwlRx Portal</span>
      </div>
    </div>
    </div>
    </div>
  )
}

function Flag({ text, accent, solid, icon }: { text: string; accent: string; solid?: boolean; icon?: React.ReactNode }) {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap',
      border: `2px solid ${accent}`, borderRadius: 5, padding: '4px 11px', fontSize: 12.5, fontWeight: 800,
      textTransform: 'uppercase', letterSpacing: 0.4,
      background: solid ? accent : 'transparent', color: solid ? '#fff' : accent,
    }}>{icon}{text}</span>
  )
}
const lbl: React.CSSProperties = { fontSize: 9, fontWeight: 800, textTransform: 'uppercase', letterSpacing: 1, color: '#555' }
